import CheckIcon from '@src/svgs/CheckIcon';
import React from 'react';
import styled, { css } from 'styled-components';

/**
 * Where the marker is painted, which decides its chrome rather than its meaning.
 *
 * - `ring` — on a panel (the Add Clips picker's rows, the Clip Producer's
 *   results). A rounded box with a hairline stroke; the surface behind it is
 *   flat and dark, so a stroke is enough to read. Boxed rather than circled and
 *   drawn thin on purpose: it sits at the head of a list row, where a heavy
 *   circle reads as the row's subject rather than as its checkbox.
 * - `frame` — on a video frame (the Video Clips card). A rounded **box** with a
 *   translucent dark fill, a white stroke and a backdrop blur: a thumbnail can
 *   be any colour at any brightness, and a bare stroke disappears over a bright
 *   one. This is the marker the card carried before, restored.
 */
export type SelectionBadgeVariant = 'ring' | 'frame';

interface SelectionOrderBadgeProps {
  /** 1-based position in the selection; `null` when not selected. */
  position: number | null;
  /** A tick instead of the number, where there is only ever one pick. */
  showTick?: boolean;
  variant?: SelectionBadgeVariant;
  /** So a caller can position the badge; the badge itself has no layout. */
  className?: string;
  'data-testid'?: string;
}

/**
 * PLAT-11093 DS1 — the multi-select marker on every surface a reel is picked
 * from: the Add Clips picker, the Clip Producer's results, and (PLAT-11092) the
 * Video Clips grid. It carries the pick *and* its position, because the
 * selection order is the reel's playback order (PLAT-11048 DE11) and a tick
 * hides the one thing the creator most needs to see.
 */
export const SelectionOrderBadge: React.FC<SelectionOrderBadgeProps> = ({
  position,
  showTick = false,
  variant = 'ring',
  className,
  'data-testid': dataTestId,
}) => (
  <Badge
    $selected={position !== null}
    $variant={variant}
    className={className}
    data-testid={dataTestId}
  >
    {position === null ? (
      ''
    ) : showTick ? (
      <CheckIcon width="9" height="9" fillColor="currentColor" />
    ) : (
      position
    )}
  </Badge>
);

const Badge = styled.span<{
  $selected: boolean;
  $variant: SelectionBadgeVariant;
}>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  /* Gold, not green: selection is a visual control, and --primary is reserved
     for actions (PLAT-11094). The number stays — the pick's position is the
     reel's playback order (PLAT-11048 DE11), which a tick would hide. */
  color: var(--background);
  line-height: 1;
  font-variant-numeric: tabular-nums;

  ${props =>
    props.$variant === 'frame'
      ? css`
          width: 22px;
          height: 22px;
          border-radius: var(--spacing-2xs);
          font-size: 11px;
          font-weight: var(--font-weight-semi-bold);
          /* The fill and blur are what make it legible over an arbitrary frame;
             a bare stroke vanishes over a bright one. */
          background: ${props.$selected
            ? 'var(--accent)'
            : 'rgba(0, 0, 0, 0.45)'};
          border: 1px solid
            ${props.$selected ? 'var(--accent)' : 'rgba(255, 255, 255, 0.6)'};
          backdrop-filter: blur(4px);
        `
      : css`
          width: 16px;
          height: 16px;
          border-radius: var(--spacing-2xs);
          font-size: 10px;
          /* A 1px --border box all but disappears on the dark card header, so
             the empty state keeps the muted stroke — at a hairline weight,
             which is enough to read as a control without announcing itself. */
          border: 1px solid
            ${props.$selected ? 'var(--accent)' : 'var(--text-muted)'};
          background-color: ${props.$selected
            ? 'var(--accent)'
            : 'transparent'};
        `}
`;
